import { Document, pdf } from '@react-pdf/renderer';
import middleware from '../Api/Middleware';
import InvoicePDFPages from './InvoicePdf';
import { formatCurrency, getCountryName, parseCurrency } from "../../utils";
import ToastPromise from '../Toasts/ToastPromise';

export const generateCreditation = async (creditationIds, print = false) => {
    return await ToastPromise({
        promise: (async () => {
            let formattedCreditationIds = creditationIds.map((id) => ({ creditation_id: id }));

            const response = await middleware.get(`orders/generateCreditations`, {
                params: { creditations: formattedCreditationIds },
            });

            const creditations = response.data.data.creditations;
            const companyInfo = response.data.data.company_info;

            if (!window.Buffer) {
                const { Buffer } = await import('buffer');
                window.Buffer = Buffer;
            }

            const formattedCreditations = [];
            let successCount = 0;
            let failedCount = 0;
            let notProcessedCount = 0;

            for (const creditationData of creditations) {
                if (creditationData.code) {
                    if (creditationData.code === 'needs_processing') {
                        notProcessedCount++;
                    } else {
                        failedCount++;
                    }
                    continue;
                }

                const creditation = creditationData.creditation;
                const order = creditationData.order;
                const invoice = creditationData.invoice;
                const customer = order.client_consumer ?? order.client_business;
                const deliveryAddressData = creditationData.delivery_address;
                const invoiceAddressData = creditationData.invoice_address;

                // Prepare product lines for credited items
                const productLines = [];

                // Add credited products
                if (creditation.creditation_products) {
                    creditation.creditation_products.forEach(creditProduct => {
                        const product = order.orders_sold_products.find(
                            p => p.sold_product_id === creditProduct.sold_product_id
                        );

                        if (product) {
                            // Calculate the credited amount
                            const creditAmount = parseCurrency(creditProduct.restitution);
                            const unitPrice = creditProduct.amount > 0
                                ? creditAmount / creditProduct.amount
                                : creditAmount;

                            productLines.push({
                                quantity: creditProduct.amount,
                                name: product.product_name,
                                description: product.product_description || '',
                                unitPrice: formatCurrency(-unitPrice), // Negative for credit
                                originalPrice: null,
                                showOriginalPrice: false,
                                totalPrice: formatCurrency(-creditAmount) // Negative for credit
                            });
                        }
                    });
                }

                // Add delivery credit if applicable
                if (creditation.delivery_restituted) {
                    productLines.push({
                        quantity: 1,
                        name: 'Verzendkosten',
                        description: null,
                        unitPrice: formatCurrency(-parseCurrency(order.delivery_cost)),
                        originalPrice: null,
                        showOriginalPrice: false,
                        totalPrice: formatCurrency(-parseCurrency(order.delivery_cost))
                    });
                }

                // Add restitution cost if applicable
                const restitutionCost = parseCurrency(creditation.restitution_cost);
                if (restitutionCost > 0) {
                    productLines.push({
                        quantity: 1,
                        name: 'Verwerkingskosten',
                        description: null,
                        unitPrice: formatCurrency(restitutionCost),
                        originalPrice: null,
                        showOriginalPrice: false,
                        totalPrice: formatCurrency(restitutionCost)
                    });
                }

                productLines.push({
                    quantity: null,
                    name: 'Reden creditering',
                    description: creditation.creditation_reason || 'Onbekende reden',
                    unitPrice: null,
                    originalPrice: null,
                    showOriginalPrice: false,
                    totalPrice: null
                });

                // Calculate totals for credit note
                const totalRestitution = parseCurrency(creditation.restitution_total) - parseCurrency(creditation.restitution_cost);
                const totalWithoutBtw = totalRestitution / 1.21; // Assuming 21% BTW
                const btwAmount = totalRestitution - totalWithoutBtw;

                const totals = {
                    subtotal: formatCurrency(-totalRestitution),
                    totalExclBtw: formatCurrency(-totalWithoutBtw),
                    btw: formatCurrency(-btwAmount),
                    totalInclBtw: formatCurrency(-totalRestitution)
                };

                // Prepare footer text for credit note
                const footerText = `Voor vragen kunt u contact opnemen via ${companyInfo.invoicemail}. Creditering van factuur ${creditationData.head_invoice?.invoice_number}`;

                // Prepare addresses with country names
                const preparedDeliveryAddress = deliveryAddressData ? {
                    ...deliveryAddressData,
                    country: getCountryName(deliveryAddressData?.country?.country_code_2)
                } : null;

                const preparedInvoiceAddress = invoiceAddressData ? {
                    ...invoiceAddressData,
                    country: getCountryName(invoiceAddressData?.country?.country_code_2)
                } : null;

                formattedCreditations.push({
                    documentNumber: invoice.invoice_number,
                    documentDate: new Date(invoice.billed_on).toLocaleDateString('nl-NL'),
                    customer,
                    companyInfo,
                    deliveryAddress: preparedDeliveryAddress,
                    invoiceAddress: preparedInvoiceAddress,
                    productLines,
                    totals,
                    footerText,
                });

                successCount++;
            }

            if (formattedCreditations.length === 0) {
                throw new Error(`Geen facturen konden worden gegenereerd. ${notProcessedCount} orders nog niet verwerkt, ${failedCount} mislukt.`);
            }

            const pdfDoc = (
                <Document>
                    {formattedCreditations.map((creditation, index) => (
                        <InvoicePDFPages
                            key={index}
                            documentType="CREDIT"
                            documentNumber={creditation.documentNumber}
                            documentDate={creditation.documentDate}
                            customer={creditation.customer}
                            company={creditation.companyInfo}
                            deliveryAddress={creditation.deliveryAddress}
                            invoiceAddress={creditation.invoiceAddress}
                            productLines={creditation.productLines}
                            totals={creditation.totals}
                            footerText={creditation.footerText}
                        />
                    ))}
                </Document>
            );

            const pdfBlob = await pdf(pdfDoc).toBlob();
            const url = URL.createObjectURL(pdfBlob);
            const printWindow = window.open(url, '_blank');
            if (print && printWindow) { // Open the printing dialog
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
            setTimeout(() => URL.revokeObjectURL(url), 100);

            return {
                success: true,
                successCount,
                notProcessedCount,
                failedCount,
                message: `${successCount} factuur(en) succesvol gegenereerd${notProcessedCount > 0 ? `, ${notProcessedCount} bestelling(en) nog niet verwerkt` : ''}${failedCount > 0 ? `, ${failedCount} mislukt` : ''}`
            };
        })(),
        pending: 'Facturen genereren…',
        successRenderer: (data) => data?.message || 'Facturen succesvol gegenereerd',
        errorRenderer: (error) =>
            error?.response?.data?.message ||
            error?.message ||
            'Er is een fout opgetreden bij het genereren van de facturen',
        autoClose: 5000,
    });
    // try {
    //     if (!window.Buffer) {
    //         const { Buffer } = await import('buffer');
    //         window.Buffer = Buffer;
    //     }

    //     // Fetch creditation data from API
    //     const creditationResponse = await middleware.get(`orders/generateCreditation/${creditationId}`);
    //     const creditationData = creditationResponse.data.data;

    //     const creditation = creditationData.creditation;
    //     const order = creditationData.order;
    //     const invoice = creditationData.invoice;
    //     const customer = order.client_consumer ?? order.client_business;
    //     const company = creditationData.company_info;
    //     const deliveryAddressData = creditationData.delivery_address;
    //     const invoiceAddressData = creditationData.invoice_address;

    //     // Prepare product lines for credited items
    //     const productLines = [];

    //     // Add credited products
    //     if (creditation.creditation_products) {
    //         creditation.creditation_products.forEach(creditProduct => {
    //             const product = order.orders_sold_products.find(
    //                 p => p.sold_product_id === creditProduct.sold_product_id
    //             );

    //             if (product) {
    //                 // Calculate the credited amount
    //                 const creditAmount = parseCurrency(creditProduct.restitution);
    //                 const unitPrice = creditProduct.amount > 0
    //                     ? creditAmount / creditProduct.amount
    //                     : creditAmount;

    //                 productLines.push({
    //                     quantity: creditProduct.amount,
    //                     name: product.product_name,
    //                     description: product.product_description || '',
    //                     unitPrice: formatCurrency(-unitPrice), // Negative for credit
    //                     originalPrice: null,
    //                     showOriginalPrice: false,
    //                     totalPrice: formatCurrency(-creditAmount) // Negative for credit
    //                 });
    //             }
    //         });
    //     }

    //     // Add delivery credit if applicable
    //     if (creditation.delivery_restituted) {
    //         productLines.push({
    //             quantity: 1,
    //             name: 'Verzendkosten',
    //             description: null,
    //             unitPrice: formatCurrency(-parseCurrency(order.delivery_cost)),
    //             originalPrice: null,
    //             showOriginalPrice: false,
    //             totalPrice: formatCurrency(-parseCurrency(order.delivery_cost))
    //         });
    //     }

    //     // Add restitution cost if applicable
    //     const restitutionCost = parseCurrency(creditation.restitution_cost);
    //     if (restitutionCost > 0) {
    //         productLines.push({
    //             quantity: 1,
    //             name: 'Verwerkingskosten',
    //             description: null,
    //             unitPrice: formatCurrency(restitutionCost),
    //             originalPrice: null,
    //             showOriginalPrice: false,
    //             totalPrice: formatCurrency(restitutionCost)
    //         });
    //     }

    //     // Add creditation reason
    //     const creditReasons = {
    //         1: "Beschadigd of defect bij ontvangst",
    //         2: "Foutieve maat of pasvorm",
    //         3: "Niet zoals beschreven",
    //         4: "Verlies van interesse of wijziging van voorkeur",
    //         5: "Verkeerde kleur of stijl"
    //     };

    //     productLines.push({
    //         quantity: null,
    //         name: 'Reden creditering',
    //         description: creditReasons[creditation.creditation_reason] || 'Onbekende reden',
    //         unitPrice: null,
    //         originalPrice: null,
    //         showOriginalPrice: false,
    //         totalPrice: null
    //     });

    //     // Calculate totals for credit note
    //     const totalRestitution = parseCurrency(creditation.restitution_total) - parseCurrency(creditation.restitution_cost);
    //     const totalWithoutBtw = totalRestitution / 1.21; // Assuming 21% BTW
    //     const btwAmount = totalRestitution - totalWithoutBtw;

    //     const totals = {
    //         subtotal: formatCurrency(-totalRestitution),
    //         totalExclBtw: formatCurrency(-totalWithoutBtw),
    //         btw: formatCurrency(-btwAmount),
    //         totalInclBtw: formatCurrency(-totalRestitution)
    //     };

    //     // Prepare footer text for credit note
    //     const footerText = `Voor vragen kunt u contact opnemen via ${company.invoicemail}. Creditering van factuur ${creditationData.head_invoice?.invoice_number}`;

    //     // Prepare addresses with country names
    //     const preparedDeliveryAddress = deliveryAddressData ? {
    //         ...deliveryAddressData,
    //         country: getCountryName(deliveryAddressData?.country?.country_code_2)
    //     } : null;

    //     const preparedInvoiceAddress = invoiceAddressData ? {
    //         ...invoiceAddressData,
    //         country: getCountryName(invoiceAddressData?.country?.country_code_2)
    //     } : null;

    //     // Generate PDF
    //     const pdfDoc = (
    //         <Document>
    //             <InvoicePDFPages
    //                 documentType="CREDIT"
    //                 documentNumber={invoice.invoice_number}
    //                 documentDate={new Date(invoice.billed_on).toLocaleDateString('nl-NL')}
    //                 customer={customer}
    //                 company={company}
    //                 deliveryAddress={preparedDeliveryAddress}
    //                 invoiceAddress={preparedInvoiceAddress}
    //                 productLines={productLines}
    //                 totals={totals}
    //                 footerText={footerText}
    //             />
    //         </Document>
    //     );

    //     const pdfBlob = await pdf(pdfDoc).toBlob();

    //     // Create download
    //     const url = URL.createObjectURL(pdfBlob);

    //     window.open(url, '_blank');

    //     return true;
    // } catch (error) {
    //     ToastError(`Er is fout gegaan tijdens het genereren van de creditfactuur. Probeer het later opnieuw.`, 'credit_error');
    //     console.error('Error generating credit invoice:', error);
    // }
};